import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import AdminLogin from './AdminLogin';
import ErrorMonitoringTest from '../ErrorMonitoringTest/ErrorMonitoringTest';
import BookingPageManager from './BookingPageManager';
import AfternoonTeaBookings from './AfternoonTeaBookings';
import { getSystemStatus, updateSystemStatus } from '../../../services/systemStatusService';
import ChildrensArtModeration from '../ChildrensArt/ChildrensArtModeration';
import ApplicationsModeration from './ApplicationsModeration';
import FaceOfBelvoirModeration from './FaceOfBelvoirModeration';
import MarketingAnalytics from './MarketingAnalytics';
// BookingPageManager handles its own imports and state

const PageContainer = styled.div`
  min-height: 100vh;
  background: ${props => props.theme.colors.offWhite};
  padding: 2rem 1.5rem 3rem;

  @media (max-width: 768px) {
    padding: 1.5rem 1rem 2rem;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1600px;
  margin: 0 auto;
`;

const Header = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem 2.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(16, 29, 64, 0.08);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    padding: 1.5rem;
    flex-direction: column;
    align-items: flex-start;
  }
`;

const HeaderLeft = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.h1`
  margin: 0 0 0.5rem;
  color: ${props => props.theme.colors.primary};
  font-size: 2.25rem;
  font-weight: 700;
  letter-spacing: -0.02em;
`;

const Subtitle = styled.p`
  margin: 0;
  color: ${props => props.theme.colors.secondary};
  font-size: 1rem;
  font-weight: 500;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const TopNav = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  background: white;
  border: 1px solid rgba(16, 29, 64, 0.1);
  border-radius: 14px;
  padding: 0.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04);
  margin-bottom: 1.5rem;
`;

const NavButton = styled.button`
  flex: 1;
  min-width: 140px;
  border: 2px solid ${props => (props.active ? props.theme.colors.primary : 'transparent')};
  background: ${props => (props.active ? props.theme.colors.primary : 'transparent')};
  color: ${props => (props.active ? 'white' : props.theme.colors.primary)};
  padding: 0.75rem 1.25rem;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.25s ease;
  position: relative;

  &:hover {
    background: ${props => (props.active ? props.theme.colors.primary : props.theme.colors.offWhite)};
    border-color: ${props => props.theme.colors.primary};
    transform: translateY(-1px);
    box-shadow: ${props => props.active ? '0 4px 12px rgba(16, 29, 64, 0.2)' : '0 2px 6px rgba(0, 0, 0, 0.08)'};
  }

  &:active {
    transform: translateY(0);
  }
`;

const DataCentreButton = styled.a`
  border: 2px solid ${props => props.theme.colors.primary};
  background: white;
  color: ${props => props.theme.colors.primary};
  padding: 0.65rem 1.25rem;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.25s ease;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: ${props => props.theme.colors.primary};
    color: white;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(16, 29, 64, 0.25);
  }

  &:active {
    transform: translateY(0);
  }
`;

const LogoutButton = styled.button`
  border: 2px solid ${props => props.theme.colors.error || '#c0392b'};
  background: white;
  color: ${props => props.theme.colors.error || '#c0392b'};
  padding: 0.65rem 1.25rem;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.25s ease;

  &:hover {
    background: ${props => props.theme.colors.error || '#c0392b'};
    color: white;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(192, 57, 43, 0.25);
  }

  &:active {
    transform: translateY(0);
  }
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 1rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(16, 29, 64, 0.08);
  padding: 1.75rem;
  transition: box-shadow 0.2s ease;
  overflow: visible;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.06);
  }

  @media (max-width: 768px) {
    padding: 1.25rem;
  }
`;

const SectionTitle = styled.h3`
  margin: 0 0 1rem;
  color: ${props => props.theme.colors.primary};
  font-size: 1.5rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DocList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const CategorySection = styled.div`
  border: 2px solid ${props => props.highlight ? props.theme.colors.accent : 'rgba(16, 29, 64, 0.1)'};
  border-radius: 12px;
  background: ${props => props.highlight ? 'rgba(157, 109, 73, 0.05)' : props.theme.colors.offWhite};
  overflow: hidden;
  transition: all 0.25s ease;
  position: relative;

  ${props => props.highlight && `
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 3px;
      background: linear-gradient(90deg, ${props.theme.colors.primary}, ${props.theme.colors.accent});
    }
  `}

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }
`;

const CategoryHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  background: ${props => props.expanded ? props.theme.colors.primary : 'transparent'};
  color: ${props => props.expanded ? 'white' : props.theme.colors.primary};
  border: none;
  cursor: pointer;
  font-weight: 700;
  font-size: 1rem;
  transition: all 0.25s ease;
  text-align: left;

  &:hover {
    background: ${props => props.expanded ? props.theme.colors.primary : props.theme.colors.offWhite};
  }
`;

const CategoryIcon = styled.span`
  margin-right: 0.75rem;
  font-size: 1.2rem;
`;

const CategoryToggle = styled.span`
  font-size: 0.9rem;
  transition: transform 0.25s ease;
  transform: ${props => props.expanded ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const CategoryDocs = styled.div`
  display: ${props => props.expanded ? 'flex' : 'none'};
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem;
  background: white;
`;

const DocButton = styled.button`
  width: 100%;
  text-align: left;
  padding: 0.85rem 1rem;
  border-radius: 10px;
  border: 2px solid ${props => (props.active ? props.theme.colors.primary : 'rgba(16, 29, 64, 0.1)')};
  background: ${props => (props.active ? props.theme.colors.primary : 'white')};
  color: ${props => (props.active ? 'white' : props.theme.colors.primary)};
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.25s ease;
  position: relative;

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background: ${props => (props.active ? props.theme.colors.primary : props.theme.colors.offWhite)};
    transform: translateX(4px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  &::before {
    content: '📄';
    margin-right: 0.5rem;
    font-size: 1rem;
  }
`;

const DocBody = styled.div`
  border: 1px solid rgba(16, 29, 64, 0.1);
  border-radius: 14px;
  background: #fffef9;
  padding: 2rem;
  min-height: 500px;
  font-family: 'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif;
  font-size: 0.97rem;
  color: #1c1c1c;
  line-height: 1.7;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.02);

  h1, h2, h3, h4 {
    margin: 0.6em 0 0.35em;
    color: ${props => props.theme.colors.primary};
  }
  h1 { font-size: 1.6rem; border-bottom: 2px solid #e8e2d7; padding-bottom: 0.25rem; }
  h2 { font-size: 1.3rem; border-left: 4px solid #e8e2d7; padding-left: 0.5rem; }
  h3 { font-size: 1.1rem; color: ${props => props.theme.colors.secondary}; }

  p { margin: 0.5em 0; }

  ul, ol { margin: 0.6em 0 0.6em 1.2em; }
  li { margin: 0.25em 0; }

  code {
    background: #f3efe7;
    padding: 2px 6px;
    border-radius: 6px;
    font-family: 'SFMono-Regular', Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
    font-size: 0.93rem;
    color: #b14e0a;
  }

  pre {
    background: #0f1b3c;
    color: #f5f7ff;
    padding: 1rem;
    border-radius: 12px;
    overflow-x: auto;
    font-family: 'SFMono-Regular', Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
    font-size: 0.92rem;
    border: 1px solid #1f2b57;
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.05);
    margin: 0.75rem 0;
  }

  pre code {
    background: transparent;
    color: #f5f7ff;
    padding: 0;
  }

  blockquote {
    margin: 0.75em 0;
    padding: 0.6em 0.9em;
    background: #f6f2ea;
    border-left: 4px solid ${props => props.theme.colors.primary};
    color: #4d4d4d;
    border-radius: 8px;
  }

  table {
    border-collapse: collapse;
    width: 100%;
    margin: 0.8em 0;
    font-size: 0.95rem;
  }
  th, td {
    border: 1px solid #e6e0d6;
    padding: 8px 10px;
    text-align: left;
  }
  th {
    background: #f3efe7;
    font-weight: 700;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.25rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const Card = styled.div`
  border: 1px solid rgba(16, 29, 64, 0.1);
  border-radius: 14px;
  padding: 1.5rem;
  background: white;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
  transition: all 0.25s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.accent});
    opacity: 0;
    transition: opacity 0.25s ease;
  }

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.06);
    transform: translateY(-2px);

    &::before {
      opacity: 1;
    }
  }
`;

const CardTitle = styled.div`
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  font-size: 1.1rem;
  margin-bottom: 0.25rem;
`;

const SmallText = styled.div`
  color: ${props => props.theme.colors.secondary};
  font-size: 0.9rem;
  line-height: 1.5;
  flex: 1;
`;

const LinkButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border-radius: 10px;
  border: 2px solid ${props => props.theme.colors.primary};
  background: ${props => props.theme.colors.primary};
  color: white;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.25s ease;
  margin-top: 0.5rem;

  &:hover {
    background: ${props => props.theme.colors.accent};
    border-color: ${props => props.theme.colors.accent};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(16, 29, 64, 0.2);
  }

  &:active {
    transform: translateY(0);
  }
`;

const Pill = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 700;
  background: ${props => {
    if (props.tag === 'PHP admin') return '#eef2ff';
    if (props.tag === 'Audit') return '#fff3cd';
    if (props.tag === 'Sync') return '#d1ecf1';
    if (props.tag === 'Log file') return '#f8d7da';
    return '#e9ecef';
  }};
  color: ${props => {
    if (props.tag === 'PHP admin') return '#3b4b9a';
    if (props.tag === 'Audit') return '#856404';
    if (props.tag === 'Sync') return '#0c5460';
    if (props.tag === 'Log file') return '#721c24';
    return '#495057';
  }};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 12px;
  background: linear-gradient(135deg, #e9f7ef 0%, #d4edda 100%);
  color: #1b7a3a;
  font-weight: 600;
  font-size: 0.9rem;
  border: 1px solid rgba(27, 122, 58, 0.2);

  &::before {
    content: '✓';
    font-size: 1rem;
    font-weight: 700;
  }
`;

const TopBranding = styled.div`
  background: linear-gradient(135deg, rgba(16, 29, 64, 0.05) 0%, rgba(157, 109, 73, 0.05) 100%);
  border-bottom: 1px solid rgba(16, 29, 64, 0.1);
  padding: 0.75rem 1.5rem;
  text-align: center;
  color: ${props => props.theme.colors.secondary};
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.5px;
  margin-bottom: 1.5rem;
  border-radius: 12px 12px 0 0;
  
  @media (max-width: 768px) {
    padding: 0.65rem 1rem;
    font-size: 0.8rem;
  }
`;

const Branding = styled.div`
  text-align: center;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(16, 29, 64, 0.1);
  color: ${props => props.theme.colors.secondary};
  font-size: 0.9rem;
  font-weight: 500;
  opacity: 0.7;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  font-size: 0.95rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid rgba(16, 29, 64, 0.1);
  border-radius: 8px;
  font-size: 0.95rem;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
  
  &:disabled {
    background: #f5f5f5;
    cursor: not-allowed;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid rgba(16, 29, 64, 0.1);
  border-radius: 8px;
  font-size: 0.95rem;
  font-family: inherit;
  min-height: 80px;
  resize: vertical;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 56px;
  height: 30px;
  
  input {
    opacity: 0;
    width: 0;
    height: 0;
  }
  
  span {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: #ccc;
    transition: 0.3s;
    border-radius: 30px;
    
    &:before {
      position: absolute;
      content: "";
      height: 22px;
      width: 22px;
      left: 4px;
      bottom: 4px;
      background: white;
      transition: 0.3s;
      border-radius: 50%;
    }
  }
  
  input:checked + span {
    background: ${props => props.theme.colors.primary};
  }
  
  input:checked + span:before {
    transform: translateX(26px);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const PrimaryButton = styled.button`
  flex: 1;
  padding: 0.85rem 1.5rem;
  border-radius: 10px;
  border: 2px solid ${props => props.theme.colors.primary};
  background: ${props => props.theme.colors.primary};
  color: white;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.25s ease;
  
  &:hover {
    background: ${props => props.theme.colors.accent};
    border-color: ${props => props.theme.colors.accent};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(16, 29, 64, 0.2);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const SecondaryButton = styled.button`
  flex: 1;
  padding: 0.85rem 1.5rem;
  border-radius: 10px;
  border: 2px solid rgba(16, 29, 64, 0.2);
  background: white;
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.25s ease;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background: ${props => props.theme.colors.offWhite};
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const PageCard = styled.div`
  background: white;
  border: 2px solid rgba(16, 29, 64, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  transition: all 0.25s ease;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }
`;

const PageCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
  gap: 1rem;
  flex-wrap: wrap;
`;

const PageTitle = styled.h4`
  margin: 0;
  font-size: 1.25rem;
  color: ${props => props.theme.colors.primary};
  font-weight: 700;
`;

const PageId = styled.div`
  font-family: 'SFMono-Regular', Menlo, Monaco, Consolas, monospace;
  font-size: 0.85rem;
  color: ${props => props.theme.colors.secondary};
  background: rgba(16, 29, 64, 0.05);
  padding: 4px 8px;
  border-radius: 6px;
  margin-top: 0.5rem;
`;

const PageActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const IconButton = styled.button`
  padding: 0.5rem 0.85rem;
  border-radius: 8px;
  border: 2px solid;
  border-color: ${props => props.variant === 'danger' ? '#c0392b' : props.theme.colors.primary};
  background: white;
  color: ${props => props.variant === 'danger' ? '#c0392b' : props.theme.colors.primary};
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.variant === 'danger' ? '#c0392b' : props.theme.colors.primary};
    color: white;
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const UrlBox = styled.div`
  background: rgba(16, 29, 64, 0.03);
  border: 1px solid rgba(16, 29, 64, 0.1);
  border-radius: 8px;
  padding: 0.75rem;
  margin-top: 0.75rem;
  font-family: 'SFMono-Regular', Menlo, Monaco, Consolas, monospace;
  font-size: 0.85rem;
  word-break: break-all;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
`;

const CopyButton = styled.button`
  padding: 0.4rem 0.75rem;
  border-radius: 6px;
  border: 1px solid ${props => props.theme.colors.primary};
  background: white;
  color: ${props => props.theme.colors.primary};
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    color: white;
  }
`;

const FeatureBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.4rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
  background: ${props => props.enabled ? '#d4edda' : '#f8d7da'};
  color: ${props => props.enabled ? '#155724' : '#721c24'};
  border: 1px solid ${props => props.enabled ? '#c3e6cb' : '#f5c6cb'};
`;

const StatusManagementCard = styled.div`
  background: white;
  border: 2px solid ${props => {
    if (props.status === 'operational') return '#28a745';
    if (props.status === 'degraded') return '#ffc107';
    if (props.status === 'outage') return '#dc3545';
    return '#e9ecef';
  }};
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const StatusHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const StatusTitle = styled.h3`
  margin: 0;
  font-size: 1.25rem;
  color: #101d40;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const StatusBadgeDisplay = styled.span`
  padding: 0.4rem 0.8rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${props => {
    if (props.status === 'operational') return '#d1fae5';
    if (props.status === 'degraded') return '#fef3c7';
    if (props.status === 'outage') return '#fee2e2';
    return '#f3f4f6';
  }} !important;
  color: ${props => {
    if (props.status === 'operational') return '#047857';
    if (props.status === 'degraded') return '#b45309';
    if (props.status === 'outage') return '#b91c1c';
    return '#6b7280';
  }} !important;
`;

const StatusDescription = styled.p`
  color: #666;
  margin: 0.5rem 0;
  line-height: 1.6;
`;

const StatusActions = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1rem;
  flex-wrap: wrap;
`;

const StatusButton = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid;
  
  ${props => {
    if (props.variant === 'operational') {
      return `
        background: #d1fae5;
        color: #047857;
        border-color: #047857;
        &:hover { background: #a7f3d0; }
      `;
    }
    if (props.variant === 'degraded') {
      return `
        background: #fef3c7;
        color: #b45309;
        border-color: #b45309;
        &:hover { background: #fde68a; }
      `;
    }
    if (props.variant === 'outage') {
      return `
        background: #fee2e2;
        color: #b91c1c;
        border-color: #b91c1c;
        &:hover { background: #fecaca; }
      `;
    }
    return `
      background: white;
      color: #666;
      border-color: #e9ecef;
      &:hover { background: #f8f9fa; }
    `;
  }}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Alert = styled.div`
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  background: ${props => {
    if (props.type === 'success') return '#d4edda';
    if (props.type === 'error') return '#f8d7da';
    return '#d1ecf1';
  }} !important;
  border: 1px solid ${props => {
    if (props.type === 'success') return '#c3e6cb';
    if (props.type === 'error') return '#f5c6cb';
    return '#bee5eb';
  }};
  color: ${props => {
    if (props.type === 'success') return '#155724';
    if (props.type === 'error') return '#721c24';
    return '#0c5460';
  }} !important;
  font-weight: 500;
  
  * {
    color: inherit !important;
  }
`;

const SecurityCenter = () => {
  const [isAuthed, setIsAuthed] = useState(() => !!localStorage.getItem('token'));
  const [activeTab, setActiveTab] = useState('docs'); // docs | logs | admin | monitoring | status | pages | tea-bookings | art-gallery
  const [systemStatus, setSystemStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [statusAlert, setStatusAlert] = useState(null);
  
  // BookingPageManager handles its own state

  // Load system status on mount and when status tab is active
  useEffect(() => {
    if (activeTab === 'status') {
      loadSystemStatus();
      const interval = setInterval(loadSystemStatus, 10000); // Refresh every 10 seconds
      return () => clearInterval(interval);
    }
  }, [activeTab]);

  // BookingPageManager component handles its own data loading

  const loadSystemStatus = async () => {
    try {
      const status = await getSystemStatus();
      setSystemStatus(status);
    } catch (error) {
      console.error('Failed to load system status:', error);
    }
  };

  const handleUpdateSystemStatus = async (systemId, updates) => {
    setLoadingStatus(true);
    setStatusAlert(null); // Clear previous alerts
    try {
      const success = await updateSystemStatus(systemId, updates);
      if (success) {
        const systemName = systemId === 'booking' ? 'Booking System' : 
                          systemId === 'website' ? 'Website' :
                          systemId === 'datacentre' ? 'Data Centre' :
                          systemId === 'membership' ? 'Membership System' : systemId;
        
        setStatusAlert({ 
          type: 'success', 
          message: `✅ ${systemName} status updated successfully! Email notification sent to jhartwell@belvoircastle.com` 
        });
        await loadSystemStatus(); // Refresh
        
        // Clear alert after 8 seconds (longer so user can read the email confirmation)
        setTimeout(() => setStatusAlert(null), 8000);
      } else {
        setStatusAlert({ 
          type: 'error', 
          message: '❌ Failed to update status. Check console for details.' 
        });
        setTimeout(() => setStatusAlert(null), 8000);
      }
    } catch (error) {
      console.error('Error updating system status:', error);
      setStatusAlert({ 
        type: 'error', 
        message: `❌ Error: ${error.message}. Check browser console for details.` 
      });
      setTimeout(() => setStatusAlert(null), 8000);
    } finally {
      setLoadingStatus(false);
    }
  };

  const handleGroupSaverDegraded = () => {
    const incidentTimestamp = new Date().toLocaleString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    handleUpdateSystemStatus('booking', {
      status: 'degraded',
      statusText: 'DEGRADED',
      description: 'Booking system is experiencing an issue with the Group Saver discount. Some discount functionality may be affected.',
      incident: {
        title: 'Problem with Group Saver Discount',
        description: 'There is a problem with the Group Saver discount. The team has been notified and is investigating.',
        timestamp: incidentTimestamp
      }
    });
  };

  // BookingPageManager component handles its own handlers

  const docCategories = useMemo(
    () => [
      {
        name: 'Data Centre Docs',
        icon: '📚',
        highlight: true,
        docs: [
          { title: 'README.md', path: '/docs/README.md' },
          { title: 'SECURITY.md', path: '/docs/SECURITY.md' },
          { title: 'HOW-IT-WORKS.md', path: '/docs/HOW-IT-WORKS.md' },
          { title: 'ARCHITECTURE.md', path: '/docs/ARCHITECTURE.md' },
          { title: 'DATA-AND-QUERIES.md', path: '/docs/DATA-AND-QUERIES.md' },
          { title: 'AUTOMATED_EMAILS_OVERVIEW.md', path: '/docs/AUTOMATED_EMAILS_OVERVIEW.md' },
          { title: 'security/RAILTAIL.md', path: '/docs/security/RAILTAIL.md' },
          { title: 'security/SSL-AND-TLS.md', path: '/docs/security/SSL-AND-TLS.md' },
          { title: 'security/ENVIRONMENT-VARIABLES.md', path: '/docs/security/ENVIRONMENT-VARIABLES.md' },
          { title: 'security/AUTHENTICATION.md', path: '/docs/security/AUTHENTICATION.md' },
          { title: 'security/MARVIN.md', path: '/docs/security/MARVIN.md' },
        ]
      },
      {
        name: 'Vennersys',
        icon: '🔗',
        highlight: true,
        docs: [
          { title: 'TRANSFER_BOOKING_VENNERSYS_API.md', path: '/vennersys/TRANSFER_BOOKING_VENNERSYS_API.md' },
          { title: 'BOOKING_SYSTEM_VENNERSYS_ENDPOINTS.md', path: '/vennersys/BOOKING_SYSTEM_VENNERSYS_ENDPOINTS.md' },
          { title: 'VENPOS_TRANSFER_AND_CORE_APIS.md', path: '/vennersys/VENPOS_TRANSFER_AND_CORE_APIS.md' },
          { title: 'vennersys README.md', path: '/vennersys/README.md' },
          { title: 'AUTOMATIC_ERROR_MONITORING.md', path: '/vennersys/AUTOMATIC_ERROR_MONITORING.md' },
          { title: 'DISCOUNT_API_SECURITY_AUDIT.md', path: '/vennersys/DISCOUNT_API_SECURITY_AUDIT.md' },
          { title: 'DISCOUNT_LOGGING_UNDO_IMPLEMENTATION.md', path: '/vennersys/DISCOUNT_LOGGING_UNDO_IMPLEMENTATION.md' },
          { title: 'venpos-discount-endpoints.md', path: '/vennersys/venpos-discount-endpoints.md' },
        ]
      },
      {
        name: 'Attractis',
        icon: '🅿️',
        highlight: true,
        docs: [
          { title: 'ATTRACTIS_VEHICLE_LOOKUP_INTEGRATION.md', path: '/ATTRACTIS_VEHICLE_LOOKUP_INTEGRATION.md' },
        ]
      },
      {
        name: 'Public Root',
        icon: '📄',
        docs: [
          { title: '3D_SECURE_PAYMENT_FIX.md', path: '/3D_SECURE_PAYMENT_FIX.md' },
          { title: '3DS_UX_FLOW.md', path: '/3DS_UX_FLOW.md' },
          { title: 'APPLE_GOOGLE_PAY_SETUP.md', path: '/APPLE_GOOGLE_PAY_SETUP.md' },
          { title: 'APPLE_PAY_DEPLOYMENT.md', path: '/APPLE_PAY_DEPLOYMENT.md' },
          { title: 'APPLE_WALLET_SETUP.md', path: '/APPLE_WALLET_SETUP.md' },
          { title: 'BOOKING_API_SECURITY.md', path: '/BOOKING_API_SECURITY.md' },
          { title: 'CUSTOMER_FIELD_MAPPING.md', path: '/CUSTOMER_FIELD_MAPPING.md' },
          { title: 'DATA_FLOW_DIAGRAM.md', path: '/DATA_FLOW_DIAGRAM.md' },
          { title: 'DEPLOYMENT_SUMMARY_FINAL.md', path: '/DEPLOYMENT_SUMMARY_FINAL.md' },
          { title: 'ERROR_NOTIFICATION_DEPLOYMENT.md', path: '/ERROR_NOTIFICATION_DEPLOYMENT.md' },
          { title: 'FAMILY_SAVER_IMPLEMENTATION.md', path: '/FAMILY_SAVER_IMPLEMENTATION.md' },
          { title: 'FARM_SHOP_COUPON_SYSTEM.md', path: '/FARM_SHOP_COUPON_SYSTEM.md' },
          { title: 'FREE_BOOKING_FIX.md', path: '/FREE_BOOKING_FIX.md' },
          { title: 'FS10_FAMILY_SAVER_IMPLEMENTATION.md', path: '/FS10_FAMILY_SAVER_IMPLEMENTATION.md' },
          { title: 'MOBILE_RESPONSIVE_ENHANCEMENTS.md', path: '/MOBILE_RESPONSIVE_ENHANCEMENTS.md' },
          { title: 'PAYMENT_ERROR_HANDLING_FIX.md', path: '/PAYMENT_ERROR_HANDLING_FIX.md' },
          { title: 'PAYMENT_FIX_SUMMARY.md', path: '/PAYMENT_FIX_SUMMARY.md' },
          { title: 'PAYMENT_ORDER_NOT_FOUND_FIX.md', path: '/PAYMENT_ORDER_NOT_FOUND_FIX.md' },
          { title: 'QUICK_STRIPE_SETUP.md', path: '/QUICK_STRIPE_SETUP.md' },
          { title: 'README-afternoon-tea.md', path: '/README-afternoon-tea.md' },
          { title: 'README.md', path: '/README.md' },
          { title: 'SECURITY_AUDIT_REPORT.md', path: '/SECURITY_AUDIT_REPORT.md' },
          { title: 'SECURITY_CONFIGURATION.md', path: '/SECURITY_CONFIGURATION.md' },
          { title: 'SECURITY_EXECUTIVE_SUMMARY.md', path: '/SECURITY_EXECUTIVE_SUMMARY.md' },
          { title: 'SECURITY_FIXES_SUMMARY.md', path: '/SECURITY_FIXES_SUMMARY.md' },
          { title: 'SECURITY_QUICK_FIXES.md', path: '/SECURITY_QUICK_FIXES.md' },
          { title: 'SECURITY_README.md', path: '/SECURITY_README.md' },
          { title: 'STRIPE_CONFIG_CHECK.md', path: '/STRIPE_CONFIG_CHECK.md' },
          { title: 'STRIPE_SETUP_INSTRUCTIONS.md', path: '/STRIPE_SETUP_INSTRUCTIONS.md' },
          { title: 'STRIPE_SETUP.md', path: '/STRIPE_SETUP.md' },
          { title: 'TESTING_3DS.md', path: '/TESTING_3DS.md' },
          { title: 'UPGRADE_DISCOUNT_CODES_NEEDED.md', path: '/UPGRADE_DISCOUNT_CODES_NEEDED.md' },
          { title: 'UPSELL_BASKET_IMPLEMENTATION.md', path: '/UPSELL_BASKET_IMPLEMENTATION.md' },
          { title: 'VENPOS_SYNC_FIX.md', path: '/VENPOS_SYNC_FIX.md' },
          { title: 'VENPOS_UPSELL_COMPLETE_FIX.md', path: '/VENPOS_UPSELL_COMPLETE_FIX.md' },
          { title: 'VENPOS_WOOCOMMERCE_SYNC_GUIDE.md', path: '/VENPOS_WOOCOMMERCE_SYNC_GUIDE.md' },
          { title: 'WALLET_EMAIL_AUTOMATION.md', path: '/WALLET_EMAIL_AUTOMATION.md' },
          { title: 'WALLET_IMPLEMENTATION_SUMMARY.md', path: '/WALLET_IMPLEMENTATION_SUMMARY.md' },
          { title: 'WEBHOOK_SETUP_CHECKLIST.md', path: '/WEBHOOK_SETUP_CHECKLIST.md' },
          { title: 'WOOCOMMERCE_VENPOS_STOCK_SYNC.md', path: '/WOOCOMMERCE_VENPOS_STOCK_SYNC.md' },
          { title: 'WOOCOMMERCE_SETUP.md', path: '/WOOCOMMERCE_SETUP.md' },
          { title: 'WOOCOMMERCE_STRIPE_SETUP.md', path: '/WOOCOMMERCE_STRIPE_SETUP.md' },
        ]
      },
      {
        name: 'API',
        icon: '🔌',
        docs: [
          { title: 'api/CRON_SETUP.md', path: '/api/CRON_SETUP.md' },
          { title: 'api/FARM_SHOP_API_SETUP.md', path: '/api/FARM_SHOP_API_SETUP.md' },
          { title: 'api/FARM_SHOP_STRIPE_SETUP.md', path: '/api/FARM_SHOP_STRIPE_SETUP.md' },
          { title: 'api/GENERATE_WC_KEYS.md', path: '/api/GENERATE_WC_KEYS.md' },
          { title: 'api/IMPORT_LIVE_DB_LOCALLY.md', path: '/api/IMPORT_LIVE_DB_LOCALLY.md' },
          { title: 'api/README.md', path: '/api/README.md' },
          { title: 'api/UPGRADE_TRACKER_DATABASE.md', path: '/api/UPGRADE_TRACKER_DATABASE.md' },
          { title: 'api/UPGRADE_TRACKER_DEBUG.md', path: '/api/UPGRADE_TRACKER_DEBUG.md' },
          { title: 'api/UPGRADE_TRACKER_SETUP.md', path: '/api/UPGRADE_TRACKER_SETUP.md' },
        ]
      },
    ],
    []
  );

  // Flatten all docs for initial selection
  const allDocs = useMemo(() => 
    docCategories.flatMap(category => category.docs),
    [docCategories]
  );

  const [selectedDoc, setSelectedDoc] = useState(allDocs[0]);
  const [expandedCategories, setExpandedCategories] = useState(() => 
    docCategories.reduce((acc, cat) => ({ ...acc, [cat.name]: true }), {})
  );
  const [docContent, setDocContent] = useState('Loading…');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [markdownReady, setMarkdownReady] = useState(false);

  // Load marked from CDN for proper markdown rendering
  useEffect(() => {
    if (window.marked) {
      setMarkdownReady(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/marked/marked.min.js';
    script.async = true;
    script.onload = () => setMarkdownReady(true);
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthed(false);
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsAuthed(!!token);
  }, []);

  useEffect(() => {
    const loadDoc = async () => {
      if (!selectedDoc) return;
      setLoading(true);
      setError('');
      try {
        const resp = await fetch(selectedDoc.path);
        if (!resp.ok) {
          throw new Error(`HTTP ${resp.status}`);
        }
        const text = await resp.text();
        const looksLikeHtml =
          /^\s*<!doctype html/i.test(text) || /^\s*<html[\s>]/i.test(text);
        if (looksLikeHtml) {
          throw new Error(`File not found at ${selectedDoc.path}`);
        }
        setDocContent(text);
      } catch (err) {
        setError(`Failed to load ${selectedDoc.title}: ${err.message}`);
        setDocContent('');
      } finally {
        setLoading(false);
      }
    };

    loadDoc();
  }, [selectedDoc]);

  const adminLinks = [
    {
      title: 'View Discounts (logs & delete)',
      href: '/admin/view-discounts.php',
      desc: 'Discount events, System1 code removal — Belvoir Admin API auth.',
      tag: 'PHP admin'
    },
    {
      title: 'View Orders (logs)',
      href: '/admin/view-orders.php',
      desc: 'Order event logs — Belvoir Admin API auth.',
      tag: 'PHP admin'
    },
    {
      title: 'Security Audit Report',
      href: '/admin/view-security-audit.php',
      desc: 'Markdown audit viewer — Belvoir Admin API auth.',
      tag: 'Audit'
    }
  ];

  const rawLogs = [
    {
      title: 'Upgrade Discount Logs (latest)',
      href: '/api/logs/upgrade-discounts-2025-12-04.log',
      desc: 'Raw server log from upgrade discount creation.',
      tag: 'Log file'
    },
    {
      title: 'Discounts JSON/Log (front-end/logs/discounts)',
      href: '/logs/discounts/discounts_2025-12-09.log',
      desc: 'Raw discount logs (if exposed by web server).',
      tag: 'Log file'
    },
    {
      title: 'Orders JSON/Log (front-end/logs/orders)',
      href: '/logs/orders/orders_2025-12-08.log',
      desc: 'Raw order logs (if exposed by web server).',
      tag: 'Log file'
    }
  ];

  if (!isAuthed) {
    return <AdminLogin onSuccess={() => setIsAuthed(true)} />;
  }

  return (
    <PageContainer>
      <ContentWrapper>
        <TopBranding>Brought to you by Winters Web</TopBranding>
        <Header>
          <HeaderLeft>
            <Title>Security Centre</Title>
            <Subtitle>Documentation, security audits, and operational monitoring</Subtitle>
          </HeaderLeft>
          <HeaderRight>
            <StatusBadge>Secure access</StatusBadge>
            <DataCentreButton 
              href="https://belvoirdatacentre.co.uk/" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              Data Centre →
            </DataCentreButton>
            <LogoutButton onClick={handleLogout}>Log Out</LogoutButton>
          </HeaderRight>
        </Header>

      <TopNav>
        <NavButton active={activeTab === 'docs'} onClick={() => setActiveTab('docs')}>Docs</NavButton>
        <NavButton active={activeTab === 'logs'} onClick={() => setActiveTab('logs')}>Logs & Audits</NavButton>
        <NavButton active={activeTab === 'admin'} onClick={() => setActiveTab('admin')}>Admin Tools</NavButton>
        <NavButton active={activeTab === 'pages'} onClick={() => setActiveTab('pages')}>Booking Pages</NavButton>
        <NavButton active={activeTab === 'tea-bookings'} onClick={() => setActiveTab('tea-bookings')}>Afternoon Tea</NavButton>
        <NavButton active={activeTab === 'monitoring'} onClick={() => setActiveTab('monitoring')}>Error Monitoring</NavButton>
        <NavButton active={activeTab === 'status'} onClick={() => setActiveTab('status')}>System Status</NavButton>
        <NavButton active={activeTab === 'art-gallery'} onClick={() => setActiveTab('art-gallery')}>🏴‍☠️ Pirate Sails</NavButton>
        <NavButton active={activeTab === 'applications'} onClick={() => setActiveTab('applications')}>💼 Applications</NavButton>
        <NavButton active={activeTab === 'face-of-belvoir'} onClick={() => setActiveTab('face-of-belvoir')}>Face of Belvoir</NavButton>
        <NavButton active={activeTab === 'marketing'} onClick={() => setActiveTab('marketing')}>Marketing Analytics</NavButton>
      </TopNav>

      {activeTab === 'docs' && (
        <Layout>
          <Panel>
            <SectionTitle>Documentation</SectionTitle>
            <SmallText style={{ marginBottom: '1.25rem' }}>Organized by category</SmallText>
            <DocList>
              {docCategories.map(category => (
                <CategorySection key={category.name} highlight={category.highlight}>
                  <CategoryHeader
                    expanded={expandedCategories[category.name]}
                    onClick={() => setExpandedCategories(prev => ({
                      ...prev,
                      [category.name]: !prev[category.name]
                    }))}
                  >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <CategoryIcon>{category.icon}</CategoryIcon>
                      {category.name}
                      <span style={{ marginLeft: '0.5rem', fontSize: '0.85rem', opacity: 0.7 }}>
                        ({category.docs.length})
                      </span>
                    </div>
                    <CategoryToggle expanded={expandedCategories[category.name]}>▼</CategoryToggle>
                  </CategoryHeader>
                  <CategoryDocs expanded={expandedCategories[category.name]}>
                    {category.docs.map(doc => (
                      <DocButton
                        key={doc.path}
                        active={selectedDoc?.path === doc.path}
                        onClick={() => setSelectedDoc(doc)}
                      >
                        {doc.title}
                      </DocButton>
                    ))}
                  </CategoryDocs>
                </CategorySection>
              ))}
            </DocList>
          </Panel>

          <Panel>
            <div style={{ marginBottom: '1.25rem' }}>
              <SectionTitle>{selectedDoc?.title || 'Document'}</SectionTitle>
              {loading && <SmallText style={{ marginTop: '0.5rem' }}>Loading…</SmallText>}
              {error && <SmallText style={{ color: '#c0392b', marginTop: '0.5rem' }}>{error}</SmallText>}
            </div>
            <DocBody
              dangerouslySetInnerHTML={{
                __html: markdownReady && window.marked
                  ? window.marked.parse(docContent || '')
                  : (docContent || '').replace(/</g, '&lt;').replace(/>/g, '&gt;')
              }}
            />
          </Panel>
        </Layout>
      )}

      {activeTab === 'logs' && (
        <Panel>
          <SectionTitle>Logs & Audits</SectionTitle>
          <SmallText style={{ marginBottom: '1.5rem' }}>Admin-protected viewers and raw logs where exposed.</SmallText>
          <Grid>
            {adminLinks.map(link => (
              <Card key={link.href}>
                <CardTitle>{link.title}</CardTitle>
                <SmallText>{link.desc}</SmallText>
                <div>
                  <Pill tag={link.tag}>{link.tag}</Pill>
                </div>
                <LinkButton href={link.href} target="_blank" rel="noopener noreferrer">
                  Open →
                </LinkButton>
              </Card>
            ))}
            {rawLogs.filter(link => link.tag !== 'Log file').map(link => (
              <Card key={link.href}>
                <CardTitle>{link.title}</CardTitle>
                <SmallText>{link.desc}</SmallText>
                <div>
                  <Pill tag={link.tag}>{link.tag}</Pill>
                </div>
                <LinkButton href={link.href} target="_blank" rel="noopener noreferrer">
                  View Raw →
                </LinkButton>
              </Card>
            ))}
          </Grid>
        </Panel>
      )}

      {activeTab === 'admin' && (
        <Panel>
          <SectionTitle>Admin Tools</SectionTitle>
          <SmallText style={{ marginBottom: '1.5rem' }}>Access existing PHP-based admin interfaces for system management.</SmallText>
          <Grid>
            <Card>
              <CardTitle>Discount Manager</CardTitle>
              <SmallText>Manage System1 codes and discount logs.</SmallText>
              <LinkButton href="/admin/view-discounts.php" target="_blank" rel="noopener noreferrer">
                Open Discount Manager
              </LinkButton>
            </Card>
            <Card>
              <CardTitle>Order Logs</CardTitle>
              <SmallText>Review order ingest logs and status.</SmallText>
              <LinkButton href="/admin/view-orders.php" target="_blank" rel="noopener noreferrer">
                Open Order Logs
              </LinkButton>
            </Card>
            <Card>
              <CardTitle>Security Audit Report</CardTitle>
              <SmallText>Rendered audit markdown via existing admin page.</SmallText>
              <LinkButton href="/admin/view-security-audit.php" target="_blank" rel="noopener noreferrer">
                Open Audit
              </LinkButton>
            </Card>
          </Grid>
        </Panel>
      )}

      {activeTab === 'pages' && (
        <Panel>
          <SectionTitle>Booking Page Management</SectionTitle>
          <SmallText style={{ marginBottom: '1.5rem' }}>
            Create and manage booking pages with custom configurations (Family Saver, URLs, etc.)
          </SmallText>
          <BookingPageManager />
        </Panel>
      )}

      {activeTab === 'tea-bookings' && (
        <AfternoonTeaBookings />
      )}

      {activeTab === 'monitoring' && (
        <Panel style={{ padding: '0' }}>
          <div style={{ padding: '1.5rem 1.5rem 0 1.5rem' }}>
            <SectionTitle>Error Monitoring & Testing</SectionTitle>
            <SmallText style={{ marginBottom: '1.5rem' }}>
              Test error triggers, monitor system status, and manage maintenance mode.
            </SmallText>
          </div>
          <div style={{ padding: '0 1.5rem 1.5rem 1.5rem' }}>
            <ErrorMonitoringTest />
          </div>
        </Panel>
      )}

      {activeTab === 'status' && (
        <Panel>
          <SectionTitle>System Status Management</SectionTitle>
          <SmallText style={{ marginBottom: '1.5rem' }}>
            View and manage the public-facing system status page. Status updates automatically send email notifications to jhartwell@belvoircastle.com.
          </SmallText>

          {statusAlert && (
            <Alert type={statusAlert.type}>
              {statusAlert.message}
            </Alert>
          )}

          {/* Current System Status */}
          {systemStatus && systemStatus.systems && (
            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem', color: '#101d40' }}>
                📊 Current System Status
              </h3>
              {systemStatus.systems.map(system => (
                <StatusManagementCard key={system.id} status={system.status}>
                  <StatusHeader>
                    <StatusTitle>
                      <span style={{ fontSize: '1.5rem' }}>{system.icon}</span>
                      {system.name}
                    </StatusTitle>
                    <StatusBadgeDisplay status={system.status}>
                      {system.statusText || system.status}
                    </StatusBadgeDisplay>
                  </StatusHeader>
                  
                  <StatusDescription>
                    {system.description}
                  </StatusDescription>

                  {system.incident && (
                    <div style={{ 
                      background: '#fff3cd', 
                      border: '1px solid #ffc107', 
                      borderRadius: '8px', 
                      padding: '1rem', 
                      marginTop: '1rem' 
                    }}>
                      <p style={{ margin: '0 0 0.5rem 0', fontWeight: '600', color: '#856404' }}>
                        ⚠️ {system.incident.title}
                      </p>
                      <p style={{ margin: '0', fontSize: '0.9rem', color: '#856404' }}>
                        {system.incident.description}
                      </p>
                      {system.incident.timestamp && (
                        <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.85rem', color: '#999' }}>
                          Detected: {system.incident.timestamp}
                        </p>
                      )}
                    </div>
                  )}

                  <StatusActions>
                    <StatusButton
                      variant="operational"
                      disabled={loadingStatus || system.status === 'operational'}
                      onClick={() => handleUpdateSystemStatus(system.id, {
                        status: 'operational',
                        statusText: 'OPERATIONAL',
                        description: `${system.name.split(' ')[0]} is fully operational.`,
                        incident: null
                      })}
                    >
                      ✅ Mark Operational
                    </StatusButton>
                    <StatusButton
                      variant="degraded"
                      disabled={loadingStatus || system.status === 'degraded'}
                      onClick={() => handleUpdateSystemStatus(system.id, {
                        status: 'degraded',
                        statusText: 'DEGRADED',
                        description: `${system.name} is experiencing degraded performance.`,
                        incident: {
                          title: 'Performance Degradation',
                          description: 'System is experiencing reduced performance. Team is investigating.',
                          timestamp: new Date().toLocaleString('en-GB', {
                            day: '2-digit',
                            month: 'long',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        }
                      })}
                    >
                      ⚠️ Mark Degraded
                    </StatusButton>
                    <StatusButton
                      variant="outage"
                      disabled={loadingStatus || system.status === 'outage'}
                      onClick={() => handleUpdateSystemStatus(system.id, {
                        status: 'outage',
                        statusText: 'OUTAGE',
                        description: `${system.name} is currently unavailable.`,
                        incident: {
                          title: 'Service Outage',
                          description: 'System is currently unavailable. Team is working to restore service.',
                          timestamp: new Date().toLocaleString('en-GB', {
                            day: '2-digit',
                            month: 'long',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        }
                      })}
                    >
                      🔴 Mark Outage
                    </StatusButton>
                  </StatusActions>
                </StatusManagementCard>
              ))}
              
              <div style={{ 
                background: '#e7f3ff', 
                border: '1px solid #2196f3', 
                borderRadius: '8px', 
                padding: '1rem', 
                marginTop: '1rem' 
              }}>
                <p style={{ margin: '0', color: '#1565c0', fontSize: '0.9rem' }}>
                  ℹ️ <strong>Note:</strong> Status updates automatically send email notifications to jhartwell@belvoircastle.com with full details.
                </p>
              </div>
            </div>
          )}

          {/* Information Section */}
          <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '1.5rem', marginBottom: '1.5rem' }}>
            <h4 style={{ margin: '0 0 1rem 0', color: '#101d40' }}>ℹ️ About System Status</h4>
            <div style={{ color: '#666', lineHeight: '1.8' }}>
              <p style={{ margin: '0 0 0.5rem 0' }}>
                <strong>Automatic Updates:</strong> The system status is automatically updated when booking errors occur (e.g., missing event metadata).
              </p>
              <p style={{ margin: '0 0 0.5rem 0' }}>
                <strong>Email Notifications:</strong> Every status change sends an email to jhartwell@belvoircastle.com with full details.
              </p>
              <p style={{ margin: '0 0 0.5rem 0' }}>
                <strong>User-Facing:</strong> Customers can view the status page at <code>/status</code> to check for known issues.
              </p>
              <p style={{ margin: '0' }}>
                <strong>Testing:</strong> Use the Error Monitoring tab to test the automatic status update functionality.
              </p>
            </div>
          </div>

          {/* Quick Actions */}
          <Grid>
            <Card>
              <CardTitle>Set Booking to Degraded: Group Saver Discount</CardTitle>
              <SmallText>One-click: set the booking system to DEGRADED with “Problem with Group Saver Discount” and send the status email to all recipients.</SmallText>
              <StatusButton
                variant="degraded"
                disabled={loadingStatus}
                onClick={handleGroupSaverDegraded}
                style={{ marginTop: '0.75rem' }}
              >
                ⚠️ Turn Booking to Degraded (Group Saver)
              </StatusButton>
            </Card>
            <Card>
              <CardTitle>View Public Status Page</CardTitle>
              <SmallText>See the public-facing system status page that customers see.</SmallText>
              <LinkButton href="/status" target="_blank" rel="noopener noreferrer">
                Open Status Page
              </LinkButton>
            </Card>
            <Card>
              <CardTitle>Test System Status</CardTitle>
              <SmallText>Test the automatic status update functionality.</SmallText>
              <LinkButton href="#" onClick={(e) => { e.preventDefault(); setActiveTab('monitoring'); }}>
                Go to Error Monitoring
              </LinkButton>
            </Card>
            <Card>
              <CardTitle>Documentation</CardTitle>
              <SmallText>Read about the booking error fix and system status integration.</SmallText>
              <LinkButton href="/BOOKING_ERROR_FIX_SUMMARY.md" target="_blank" rel="noopener noreferrer">
                View Documentation
              </LinkButton>
            </Card>
          </Grid>
        </Panel>
      )}


      {activeTab === 'art-gallery' && (
        <Panel>
          <SectionTitle>🏴‍☠️ Pirate Sail Competition — Submissions</SectionTitle>
          <SmallText style={{ marginBottom: '1.5rem' }}>
            Review children's pirate sail design submissions for the Pirate Circus competition.
            Approve entries to display them in the public gallery. The winning design will be made
            into a real sail for the pirate ship in the adventure playground.
          </SmallText>
          <ChildrensArtModeration />
        </Panel>
      )}

      {activeTab === 'applications' && (
        <Panel>
          <SectionTitle>💼 Job Applications</SectionTitle>
          <SmallText style={{ marginBottom: '1.5rem' }}>
            Review, triage, and manage applications for the Digital Marketing Assistant internship.
            Click any application to expand it and view the CV, cover letter, cover note, and contact details.
            Documents open in a new tab. Update statuses to track candidates through the process.
          </SmallText>
          <ApplicationsModeration />
        </Panel>
      )}

      {activeTab === 'face-of-belvoir' && (
        <Panel>
          <SectionTitle>Be the Face of Belvoir — Entries</SectionTitle>
          <SmallText style={{ marginBottom: '1.5rem' }}>
            Review and manage competition entries for the "Be the Face of Belvoir" campaign.
            Click any entry to expand it, view the family photo, update the status, and add internal notes.
            The winning family will star in Belvoir Castle's new promotional video and receive a year's membership.
          </SmallText>
          <FaceOfBelvoirModeration />
        </Panel>
      )}

      {activeTab === 'marketing' && (
        <Panel>
          <SectionTitle>Marketing Analytics</SectionTitle>
          <SmallText style={{ marginBottom: '1.5rem' }}>
            First-party traffic and conversion data stored in MySQL. Tracks sessions, returning visitors,
            UTM attribution, ticket bookings and membership sign-ups. Data accumulates from when the tracker
            was deployed — historical data is not backfilled.
          </SmallText>
          <MarketingAnalytics />
        </Panel>
      )}

        <Branding>Brought to you by Winters Web</Branding>
      </ContentWrapper>
    </PageContainer>
  );
};

export default SecurityCenter;

